import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  Alert,
  Typography,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DescriptionIcon from "@mui/icons-material/Description";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { useAuth } from "../contexts/AuthContext";

interface CommentFormProps {
  postId: string;
  onSuccess?: () => void;
}

const CommentForm: React.FC<CommentFormProps> = ({ postId, onSuccess }) => {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [video, setVideo] = useState<string>("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [pdf, setPdf] = useState<string>("");
  const [audio, setAudio] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  // Helper: Upload a single photo file
  const uploadPhoto = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    setUploadingPhoto(true);
    try {
      const res = await fetch("/api/upload/image", {
        method: "POST",
        headers: {
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: formData,
      });
      const data = await res.json();
      if (data.url || data.image_url) {
        setPhotos((prev) => [...prev, data.url || data.image_url]);
      } else {
        throw new Error("No image URL returned");
      }
    } catch (e) {
      setError("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Helper: Upload a video file
  const uploadVideo = async (file: File) => {
    console.log(
      "Starting video upload for file:",
      file.name,
      "Size:",
      file.size
    );
    const formData = new FormData();
    formData.append("file", file);
    setUploadingVideo(true);
    try {
      console.log("Sending video upload request to /api/upload/video");
      const res = await fetch("/api/upload/video", {
        method: "POST",
        headers: {
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: formData,
      });
      console.log("Video upload response status:", res.status);
      console.log(
        "Video upload response headers:",
        Object.fromEntries(res.headers.entries())
      );

      if (res.status === 413) {
        setError("Video file is too large (max 100MB)");
        return;
      }

      const data = await res.json();
      console.log("Video upload response data:", data);

      if (data.url) {
        setVideo(data.url);
        console.log("Video upload successful, URL:", data.url);
      } else {
        throw new Error("No video URL returned");
      }
    } catch (e: any) {
      console.error("Video upload error:", e);
      if (
        e?.message?.includes("413") ||
        e?.message?.toLowerCase().includes("entity too large") ||
        e?.message?.toLowerCase().includes("file too large")
      ) {
        setError("Video file is too large (max 100MB)");
      } else {
        setError("Failed to upload video");
      }
    } finally {
      setUploadingVideo(false);
    }
  };

  // Helper: Upload a PDF file
  const uploadPdf = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    setUploadingPdf(true);
    try {
      const res = await fetch("/api/upload/pdf", {
        method: "POST",
        headers: {
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        setPdf(data.url);
      } else {
        throw new Error("No PDF URL returned");
      }
    } catch (e) {
      setError("Failed to upload PDF");
    } finally {
      setUploadingPdf(false);
    }
  };

  // Helper: Upload an audio file
  const uploadAudio = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    // For audio files, check if we should skip transcription
    const maxSizeForTranscription = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSizeForTranscription) {
      formData.append("skip_transcription", "true");
      console.log(
        `Audio file ${file.name} is ${file.size} bytes, skipping transcription`
      );
    }

    setUploadingAudio(true);
    try {
      const res = await fetch("/api/upload/audio", {
        method: "POST",
        headers: {
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        setAudio(data.url);
      } else {
        throw new Error("No audio URL returned");
      }
    } catch (e) {
      setError("Failed to upload audio");
    } finally {
      setUploadingAudio(false);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      Array.from(e.target.files).forEach(uploadPhoto);
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadVideo(e.target.files[0]);
    }
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadPdf(e.target.files[0]);
    }
  };

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadAudio(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError("You must be logged in to comment");
      return;
    }
    if (!content.trim()) {
      setError("Please enter a comment");
      return;
    }

    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      const response = await fetch(`/api/blog/${postId}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user!.token}`,
        },
        body: JSON.stringify({
          content: content.trim(),
          video: video || undefined,
          photos: photos.length > 0 ? photos : undefined,
          pdf: pdf || undefined,
          audio: audio || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        // If detail is an array, join messages
        if (Array.isArray(data.detail)) {
          setError(data.detail.map((e: any) => e.msg).join(" | "));
        } else {
          setError(data.detail || "Failed to post comment");
        }
        return;
      }

      setSuccess(true);
      setContent("");
      setVideo("");
      setPhotos([]);
      setPdf("");
      setAudio("");
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Leave a Comment
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Comment submitted! It will be visible after approval.
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <TextField
          label="Your Comment"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          fullWidth
          required
          multiline
          minRows={3}
          sx={{ mb: 2 }}
        />

        {/* Video upload */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            disabled={uploadingVideo}
            sx={{ mr: 2 }}
          >
            {uploadingVideo
              ? "Uploading Video..."
              : video
              ? "Replace Video"
              : "Upload Video"}
            <input
              type="file"
              accept="video/*"
              hidden
              onChange={handleVideoChange}
            />
          </Button>
          {video && (
            <video
              src={video}
              controls
              style={{
                maxWidth: 200,
                maxHeight: 120,
                display: "block",
                marginTop: 8,
              }}
            />
          )}
        </Box>

        {/* Photo upload */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            disabled={uploadingPhoto}
            startIcon={<PhotoCamera />}
          >
            {uploadingPhoto ? "Uploading..." : "Upload Photos"}
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handlePhotoChange}
            />
          </Button>
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            {photos.map((url, idx) => (
              <img
                key={idx}
                src={url}
                alt={`${idx + 1}`}
                style={{ maxWidth: 60, maxHeight: 60, borderRadius: 4 }}
              />
            ))}
          </Box>
        </Box>

        {/* PDF upload */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            disabled={uploadingPdf}
            startIcon={<DescriptionIcon />}
          >
            {uploadingPdf ? "Uploading..." : pdf ? "Replace PDF" : "Upload PDF"}
            <input
              type="file"
              accept="application/pdf"
              hidden
              onChange={handlePdfChange}
            />
          </Button>
          {pdf && (
            <Box sx={{ mt: 1 }}>
              <a
                href={pdf}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", gap: 4 }}
              >
                <DescriptionIcon color="primary" />
                <span>View PDF</span>
              </a>
            </Box>
          )}
        </Box>

        {/* Audio upload */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            disabled={uploadingAudio}
            startIcon={<VolumeUpIcon />}
          >
            {uploadingAudio
              ? "Uploading..."
              : audio
              ? "Replace Audio"
              : "Upload Audio"}
            <input
              type="file"
              accept="audio/*"
              hidden
              onChange={handleAudioChange}
            />
          </Button>
          {audio && (
            <Box sx={{ mt: 1 }}>
              <audio
                src={audio}
                controls
                style={{
                  maxWidth: 300,
                  display: "block",
                }}
              />
            </Box>
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? "Posting..." : "Post Comment"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default CommentForm;
